import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCodeBranch,
  faInfoCircle,
  faCheck,
} from '@fortawesome/pro-solid-svg-icons'
import { useState, useRef, useEffect } from 'react'
import { getSettingsPanel } from 'apps/builder/components/settings/modal/fields'
import { Conditions } from 'apps/builder/components/conditions'
import { Calculation } from 'apps/builder/components/settings/modal/fields/calculation'
import { Choices } from 'apps/builder/components/choices'
import { eligibleForReferenceData } from 'apps/builder/lib/utils'
import { ReferenceData } from 'apps/builder/components/referencedata'
import {
  referenceDataFeatureFlag,
  builderTypeSelector,
} from 'apps/builder/store/selectors'
import { useSelector } from 'react-redux'
import { getMoreSettings } from 'apps/builder/components/settings/modal/more'
import { getWebFormsMoreSettings } from 'apps/web_form_builder/components/settings/more'

export const NonInputFieldLabelWrapper = ({
  label = '',
  error = false,
  errorText = 'This field is required',
  required = false,
  children,
  className = '',
}) => {
  return (
    <div className="non-input-label-wrapper">
      <label
        className={`non-input-label ${error ? 'invalid' : ''} ${className}`}
      >
        {required ? `${label}*` : `${label}`}
      </label>
      {children}
      {error && (
        <div className="non-input-label__error">
          <i className="non-input-label__error-icon icon--solid foundry-icon icon--exclamation-triangle icon--red"></i>
          <span className="non-input-label__error-message">{errorText}</span>
        </div>
      )}
    </div>
  )
}

export const FancyDropdown = ({
  label = '',
  error = false,
  required = false,
  className = '',
  errorText = 'This field is required',
  children,
}) => {
  return (
    <div className={`foundry--exposed-dropdown fancy--dropdown ${error ? 'foundry--field-error' : ''} ${className}`}>
      <div class="dropdown-input__wrapper foundry--input-fields">
        {children}
        {label && (
          <label className="input-fields__label">
            {required ? `${label}*` : `${label}`}
          </label>
        )}
        {error && (
          <div className="field-error--wrap">
            <i className="field-error--icon icon--solid foundry-icon icon--exclamation-triangle"></i>
            <span className="field-error--message">{errorText}</span>
          </div>
        )}
      </div>
    </div>
  )
}

export const FancyCheckbox = ({
  id,
  required = false,
  checked = false,
  disabled = false,
  error = false,
  errorText = 'This field is required',
  onChange = () => {},
  onBlur = () => {},
  additionalClass = '',
  label = '',
  ...otherProps
}) => {
  return (
    <div className="foundry--checkbox">
      <input
        id={id}
        className={`checkbox__input ${additionalClass} ${
          error ? 'invalid' : ''
        }`}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        onBlur={onBlur}
        {...otherProps}
      />
      <label className="checkbox__label" htmlFor={id}>
        <span
          className={
            required ? 'checkbox__label-text-required' : 'checkbox__label-text'
          }
        >
          {label}
        </span>
      </label>
      {error && (
        <div className="checkbox__error">
          <i className="checkbox__error-icon foundry-icon icon--solid icon--exclamation-triangle icon--red"></i>
          <span className="checkbox__error-message">{errorText}</span>
        </div>
      )}
    </div>
  )
}

export const FancyPillButton = ({
  id,
  label = '',
  className = '',
  onClick = () => {},
  onBlur = () => {},
  ...rest
}) => {
  return (
    <button
      id={id}
      className={`multi-choice--pill-button ${className}`}
      onClick={onClick}
      onBlur={onBlur}
      {...rest}
    >
      <FontAwesomeIcon className="check--icon" icon={faCheck} />
      {label}
    </button>
  )
}

export const InlineTooltip = ({
  tooltip,
  icon,
  direction = 'left',
  children,
}) => (
  <div className="foundry--tooltip--wrapper toggles__tooltip">
    <div
      className={`foundry--tooltip tooltip--${direction} tooltip--hover list-type-tooltip`}
    >
      {tooltip}
    </div>
    <div className="foundry--tooltip-icon">
      {
        children
          ? children
          : <FontAwesomeIcon icon={icon} />
      }
    </div>
  </div>
)

export const FancyToggle = ({
  id,
  label,
  subLabel = null,
  active,
  onChange = () => {},
  inlineStyle = false,
  inlineBorderStyle = false,
  inlineBorderTooltipStyle = false,
  disabled = false,
  icon = null,
  tooltip = null,
  tooltipDirection,
  className: customClass,
}) => {
  const formatID = `${label.split(' ').join('-').toLowerCase()}-field-${id}`
  return (
    <div
      className={`foundry--toggles ${
        inlineBorderStyle || inlineBorderTooltipStyle
          ? 'toggles-inline toggles-border label--wrap'
          : inlineStyle
          ? 'toggles-inline'
          : ''
      } ${disabled ? 'disabled' : ''} ${customClass && customClass}`}
    >
      <input
        className="toggles__input"
        id={formatID}
        type="checkbox"
        checked={!!active}
        onChange={({ target }) => {
          const { checked } = target
          onChange(checked)
        }}
        disabled={disabled}
      ></input>
      <label className="toggles__label" htmlFor={formatID}>
        <div className={`toggles__text ${subLabel ? 'toggles__subtext' : ''}`}>
          {label}
          {subLabel && <div className="toggles__text--small">{subLabel}</div>}
          {icon}
          {tooltip && (
            <InlineTooltip
              tooltip={tooltip}
              icon={faInfoCircle}
              direction={tooltipDirection}
            />
          )}
        </div>
      </label>
    </div>
  )
}

export const FancyRadio = ({
  id,
  value,
  label,
  disabled,
  selected,
  onChange = () => {},
  tooltip,
  tooltipDirection,
}) => {
  const formatID = `field-radio-${id}`

  return (
    <div
      className={`foundry--radiobutton ${disabled ? 'disabled' : ''}`}
      key={formatID}
    >
      <input
        className="radiobutton__input"
        id={formatID}
        type="radio"
        disabled={disabled}
        value={value}
        checked={selected == value}
        onChange={({ target }) => {
          const { checked, value } = target
          onChange(checked, value)
        }}
      ></input>
      <label className="radiobutton__label" htmlFor={formatID}>
        <span className="radiobutton__label-text">{label}</span>
        {tooltip && (
          <InlineTooltip
            tooltip={tooltip}
            icon={faInfoCircle}
            direction={tooltipDirection}
          />
        )}
      </label>
    </div>
  )
}

const FieldOptions = field => {
  const { type } = field
  const FieldSettings = getSettingsPanel(type)

  return (
    <div className="settings--field">
      <FieldSettings {...field} />
    </div>
  )
}

const ConditionOptions = ({ guid, label, sheet_id }) => (
  <Conditions
    guid={guid}
    sheet_id={sheet_id}
    conditionType={`field`}
    name={label}
  />
)

const CalculationOptions = entry => <Calculation {...entry} />

const ChoiceOptions = entry => {
  return <Choices {...entry} />
}

const ReferenceDataOptions = entry => {
  return <ReferenceData {...entry} />
}

const MoreSettingsOptions = entry => {
  const builderType = useSelector(builderTypeSelector)
  const MoreSettings =
    builderType == 'builder'
      ? getMoreSettings(entry)
      : getWebFormsMoreSettings(entry)
  return (
    <div className="settings--field">
      <MoreSettings {...entry} />
    </div>
  )
}
const NavItem = ({ type, label, active, action, icon }) => (
  <div
    onClick={() => action(type)}
    className={`settings--tab ${active ? 'active' : ''}`}
  >
    {icon} {label}
  </div>
)

export const FieldSettings = ({ entry }) => {
  const { type, keyField } = entry
  const isRefDataEnabled = useSelector(referenceDataFeatureFlag)

  const builderType = useSelector(builderTypeSelector)

  const settingsTabs = {
    field: FieldOptions,
    conditions: ConditionOptions,
    calculation: CalculationOptions,
    ['single choice']: ChoiceOptions,
    dropdown: ChoiceOptions,
    ['multiple choice']: ChoiceOptions,
    ...(isRefDataEnabled && { ['reference data']: ReferenceDataOptions }),
    ['more']: MoreSettingsOptions,
  }

  // Can add more tabs if needed in the future
  const optionalTabs = [
    {
      label: 'Calculation',
      type: 'calculation',
    },
    {
      label: 'Choices',
      type: 'single choice',
    },
    {
      label: 'Choices',
      type: 'multiple choice',
    },
    {
      label: 'Choices',
      type: 'dropdown',
    }
  ]

  let hasOptionalTabs = []

  // Switch case statement allows for us to dynamically handle multiple field types and control order of the tabs
  switch (type) {
    case 'Calculation':
    case 'Sum':
      hasOptionalTabs = [
        ...hasOptionalTabs,
        optionalTabs.find(optionalTab => optionalTab.type === 'calculation'),
      ]
      break
    case 'Single Choice':
      hasOptionalTabs = [
        ...hasOptionalTabs,
        optionalTabs.find(optionalTab => optionalTab.type === 'single choice'),
      ]
      break
    case 'Dropdown':
      hasOptionalTabs = [
        ...hasOptionalTabs,
        optionalTabs.find(optionalTab => optionalTab.type === 'single choice'),
      ]
      break
    case 'Multiple Choice':
      hasOptionalTabs = [
        ...hasOptionalTabs,
        optionalTabs.find(
          optionalTab => optionalTab.type === 'multiple choice'
        ),
      ]
      break
    default:
      hasOptionalTabs = []
  }

  const tabs = [
    ...hasOptionalTabs,
    {
      label: 'General',
      type: 'field',
    },
    !keyField && {
      label: (
        <span>
          <FontAwesomeIcon icon={faCodeBranch} />
          {` Conditions`}
        </span>
      ),
      type: 'conditions',
    },
    builderType !== 'web-form' &&
      isRefDataEnabled &&
      eligibleForReferenceData(entry) && {
        label: 'Reference Data',
        type: 'reference data',
      },
    type !== 'Static Image' && {
      label: 'More',
      type: 'more',
    },
  ].filter(exists => exists)

  const initialActiveTab = tabs[0].type
  const [activeTab, setActiveTab] = useState(initialActiveTab)
  const settingsPanel = useRef(null)
  let ActiveTab = settingsTabs[activeTab]

  useEffect(() => {
    setActiveTab(tabs[0].type)
    ActiveTab = settingsTabs[activeTab]
  }, [tabs.length])

  return (
    <>
      <div className="settings--panel" ref={settingsPanel}>
        <div className="settings--nav-wrap">
          <nav className="settings--nav control-group">
            {tabs.map(({ label, type, icon = <></> }) => {
              const props = {
                label,
                type,
                icon,
                action: setActiveTab,
                active: type.toLowerCase() === activeTab,
              }
              return <NavItem key={`nav-item-${label}`} {...props} />
            })}
          </nav>
        </div>
      </div>
      <div
        className={`${
          activeTab === 'conditions'
            ? 'conditions--content'
            : 'settings--content'
        }`}
      >
        <ActiveTab {...entry} />
      </div>
    </>
  )
}
